// Weather safety (beta) — the page behind the "Safety" tab.
//
// Lists an event's assessments and opens one (kept in the URL as ?assessment=id
// so a refresh or a shared link lands back on it). Everything this feature does
// lives under src/safety/; see backend/app/safety/__init__.py for the design.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { API_BASE_URL } from "../config";
import { useFullScreenDialog } from "../useFullScreenDialog";
import AssessmentView from "./AssessmentView";
import { SeverityChip } from "./ForecastsSection";
import { SAFETY_API, VERDICT_LABEL, fmtDateTime, fmtWindow, fromLocalInput, toLocalInput } from "./format";

const EVENTS_API = `${API_BASE_URL}/events`;

// The event to open on: the next one starting today or later, else the latest.
function defaultEvent(events) {
  const today = new Date().toISOString().slice(0, 10);
  const dated = events.filter(e => e.start_date);
  const upcoming = dated.filter(e => e.start_date >= today)
    .sort((a, b) => a.start_date.localeCompare(b.start_date));
  if (upcoming.length) return upcoming[0].id;
  const past = dated.sort((a, b) => b.start_date.localeCompare(a.start_date));
  return past[0]?.id ?? events[0]?.id ?? "";
}

export default function SafetyForecastPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = Number(searchParams.get("assessment")) || null;
  const [config, setConfig] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    axios.get(`${SAFETY_API}/config`).then(res => setConfig(res.data)).catch(() => {});
    axios.get(`${EVENTS_API}/`).then(res => {
      const list = res.data || [];
      setEvents(list);
      setEventId(id => id || defaultEvent(list));
    }).catch(() => setEvents([]));
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    axios.get(`${SAFETY_API}/assessments`, { params: { event_id: eventId } })
      .then(res => setRows(res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const open = (id) => setSearchParams(id ? { assessment: String(id) } : {});
  const event = useMemo(() => events.find(e => e.id === eventId), [events, eventId]);

  if (openId) {
    return (
      <AssessmentView
        id={openId}
        config={config}
        onBack={() => { open(null); load(); }}
        onOpenEvent={(evId) => setEventId(evId)}
      />
    );
  }

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: "1px solid", borderColor: "divider" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
        <Typography variant="h5">Weather safety</Typography>
        <Chip label="Beta" color="secondary" size="small" />
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Pull forecasts for an event's locations, compare them against threshold bands, and record
        the decision and mitigations with the forecasts it was based on.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2, alignItems: { sm: "center" } }}>
        <TextField select label="Event" value={eventId} onChange={e => setEventId(e.target.value)}
                   sx={{ minWidth: 280 }} size="small">
          {events.map(e => <MenuItem key={e.id} value={e.id}>{e.label_long || e.name}</MenuItem>)}
        </TextField>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!eventId}
                onClick={() => setCreating(true)}>
          New assessment
        </Button>
      </Stack>

      {loading ? <CircularProgress size={24} /> : rows.length === 0 ? (
        <Alert severity="info">No assessments for this event yet.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Window</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Latest forecast</TableCell>
              <TableCell>Decision</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover sx={{ cursor: "pointer" }} onClick={() => open(r.id)}>
                <TableCell>{r.title}</TableCell>
                <TableCell>{fmtWindow(r)}</TableCell>
                <TableCell>
                  <Chip size="small" label={r.status} color={r.status === "Decided" ? "success" : "default"} />
                </TableCell>
                <TableCell>
                  {r.latest_fetched_at ? (
                    <>
                      <SeverityChip severity={r.latest_severity} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {fmtDateTime(r.latest_fetched_at)}
                        {r.latest_verdict ? ` · ${VERDICT_LABEL[r.latest_verdict].toLowerCase()}` : ""}
                      </Typography>
                    </>
                  ) : "—"}
                  {r.auto_refresh && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {r.next_refresh_at ? `Next auto-refresh ${fmtDateTime(r.next_refresh_at)}` : "Auto-refresh: none left"}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {r.decision || "—"}
                  {r.decided_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {r.decided_by_name}, {fmtDateTime(r.decided_at)}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <NewAssessmentDialog open={creating} event={event} onClose={() => setCreating(false)}
                           onCreated={(a) => { setCreating(false); open(a.id); }} />
    </Paper>
  );
}

function NewAssessmentDialog({ open, event, onClose, onCreated }) {
  const fullScreen = useFullScreenDialog();
  const [form, setForm] = useState({ title: "", start: "", end: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !event) return;
    const day = event.start_date || new Date().toISOString().slice(0, 10);
    const title = `${event.label_long || event.name} — race day`;
    setForm({ title, start: `${day}T07:00`, end: `${day}T18:00` });
    // With timed routes, default to the first day any route starts: earliest
    // start to latest close of the routes starting that day. A stage race's
    // later days, or another day's distances, are then their own assessments.
    let live = true;
    axios.get(`${API_BASE_URL}/event-routes/`, { params: { event_id: event.id }, suppressErrorToast: true })
      .then(res => {
        const timed = (res.data || []).filter(r => r.start_at && r.close_at);
        if (!live || !timed.length) return;
        const localDay = (iso) => toLocalInput(iso).slice(0, 10);
        const first = timed.map(r => localDay(r.start_at)).sort()[0];
        const that = timed.filter(r => localDay(r.start_at) === first);
        const start = that.map(r => r.start_at).sort()[0];
        const end = that.map(r => r.close_at).sort().slice(-1)[0];
        setForm(f => ({ ...f, start: toLocalInput(start), end: toLocalInput(end),
                        title: that.length === 1 ? `${event.label_long || event.name} — ${that[0].name}` : f.title }));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [open, event]);

  const valid = form.title.trim() && form.start && form.end && form.end > form.start;
  const save = () => {
    setSaving(true);
    axios.post(`${SAFETY_API}/assessments`, {
      event_id: event.id, title: form.title.trim(),
      window_start: fromLocalInput(form.start), window_end: fromLocalInput(form.end),
    }).then(res => onCreated(res.data)).catch(() => {}).finally(() => setSaving(false));
  };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>New weather assessment</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" value={form.title} onChange={set("title")} autoFocus />
          <Typography variant="body2" color="text.secondary">
            The window is when participants are out on the course — the forecast is judged over it.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField label="From" type="datetime-local" value={form.start} onChange={set("start")}
                       slotProps={{ inputLabel: { shrink: true } }} />
            <TextField label="To" type="datetime-local" value={form.end} onChange={set("end")}
                       slotProps={{ inputLabel: { shrink: true } }}
                       error={!!form.start && !!form.end && form.end <= form.start} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!valid || saving}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}
